import React from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, MapPin, Headphones, Zap, Shield, Leaf, ArrowRight, Award } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { advantages, products } from '../data/mockData';
import { useLanguage } from '../contexts/LanguageContext';

const iconMap = {
  CheckCircle2,
  MapPin,
  Headphones,
  Zap,
  Shield,
  Leaf
};

export const Home = () => {
  const { t } = useLanguage();
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 to-white overflow-hidden">
        <div className="absolute inset-0 z-0">
          <div className="absolute top-20 left-10 w-72 h-72 bg-[rgb(1,157,180)] opacity-5 rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-[rgb(1,157,180)] opacity-5 rounded-full blur-3xl"></div>
        </div>
        
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <a 
                href="https://plastictechnology.lt/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center space-x-2 bg-[rgb(1,157,180)]/10 px-4 py-2 rounded-full mb-6 hover:bg-[rgb(1,157,180)]/20 transition-colors"
              >
                <Award className="h-4 w-4" style={{ color: 'rgb(1, 157, 180)' }} />
                <span className="text-sm font-medium" style={{ color: 'rgb(1, 157, 180)' }}>
                  Офіційний представник Plastic Technology Литва
                </span>
              </a>
              
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
                {t('hero.title')}
              </h1>
              
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                {t('hero.subtitle')}
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  asChild
                  size="lg"
                  style={{ backgroundColor: 'rgb(1, 157, 180)' }}
                  className="text-white hover:opacity-90 transition-opacity"
                >
                  <Link to="/contacts">
                    {t('hero.consultation')}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
                <Button asChild size="lg" variant="outline">
                  <Link to="/products">{t('hero.viewProducts')}</Link>
                </Button>
              </div>
              
              <div className="mt-12 grid grid-cols-3 gap-6">
                <div>
                  <div className="text-3xl font-bold" style={{ color: 'rgb(1, 157, 180)' }}>5</div>
                  <div className="text-sm text-gray-600">{t('hero.years')}</div>
                </div>
                <div>
                  <div className="text-3xl font-bold" style={{ color: 'rgb(1, 157, 180)' }}>98%</div>
                  <div className="text-sm text-gray-600">{t('hero.cleaning')}</div>
                </div>
                <div>
                  <div className="text-3xl font-bold" style={{ color: 'rgb(1, 157, 180)' }}>50+</div>
                  <div className="text-sm text-gray-600">{t('hero.exploitation')}</div>
                </div>
              </div>
            </div>
            
            <div className="relative">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="/images/pura-tanks-1.jpg"
                  alt="Системи очищення води PURA"
                  className="w-full h-[500px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Video Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {t('video.title')}
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              {t('video.subtitle')}
            </p>
          </div>
          <div className="max-w-5xl mx-auto">
            <div className="relative rounded-2xl overflow-hidden shadow-2xl" style={{ paddingBottom: '56.25%' }}>
              <iframe 
                className="absolute top-0 left-0 w-full h-full"
                src="https://www.youtube.com/embed/9e9o9MN8xc8?autoplay=1&mute=1&loop=1&playlist=9e9o9MN8xc8&controls=1"
                title="Виробництво PURA"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
            </div>
          </div>
        </div>
      </section>

      {/* Advantages Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {t('advantages.title')}
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              {t('advantages.subtitle')}
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {advantages.map((advantage) => {
              const Icon = iconMap[advantage.icon];
              return (
                <Card key={advantage.id} className="border-none shadow-lg hover:shadow-xl transition-shadow duration-300">
                  <CardContent className="p-6">
                    <div className="flex items-start space-x-4">
                      <div className="flex-shrink-0 w-12 h-12 rounded-lg flex items-center justify-center" style={{ backgroundColor: 'rgb(1, 157, 180, 0.1)' }}>
                        <Icon className="h-6 w-6" style={{ color: 'rgb(1, 157, 180)' }} />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">{advantage.title}</h3>
                        <p className="text-sm text-gray-600 leading-relaxed">{advantage.description}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Products Preview Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {t('products.title')}
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              {t('products.subtitle')}
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {products.map((product) => (
              <Card key={product.id} className="overflow-hidden border-none shadow-lg hover:shadow-2xl transition-all duration-300 group">
                <div className="relative h-64 overflow-hidden">
                  <img
                    src={product.image}
                    alt={product.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"></div>
                  <div className="absolute bottom-4 left-4 right-4">
                    <h3 className="text-white font-semibold text-lg">{product.title}</h3>
                  </div>
                </div>
                <CardContent className="p-6">
                  <p className="text-gray-600 text-sm mb-4 line-clamp-3">{product.description}</p>
                  <Link
                    to="/products"
                    className="inline-flex items-center text-sm font-medium transition-colors"
                    style={{ color: 'rgb(1, 157, 180)' }}
                  >
                    Дізнатися більше
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
          
          <div className="text-center mt-12">
            <Button
              asChild
              size="lg"
              style={{ backgroundColor: 'rgb(1, 157, 180)' }}
              className="text-white hover:opacity-90 transition-opacity"
            >
              <Link to="/products">{t('products.viewAll')}</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* PURA Station Details Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                {t('puraDetails.stationTitle')}
              </h2>
              <p className="text-lg text-gray-600 mb-4 leading-relaxed">
                {t('puraDetails.stationDesc1')}
              </p>
              <p className="text-lg text-gray-600 leading-relaxed">
                {t('puraDetails.stationDesc2')}
              </p>
            </div>
            <div>
              <img
                src="/images/pura-certificate.jpg"
                alt="Сертифікат PURA"
                className="rounded-2xl shadow-2xl w-full"
              />
            </div>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1">
              <img
                src="/images/pura-ce-declaration.jpg"
                alt="CE Declaration PURA"
                className="rounded-2xl shadow-2xl w-full"
              />
            </div>
            <div className="order-1 lg:order-2">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                {t('puraDetails.individualTitle')}
              </h2>
              <p className="text-lg text-gray-600 mb-4 leading-relaxed">
                {t('puraDetails.individualDesc1')}
              </p>
              <p className="text-lg text-gray-600 mb-4 leading-relaxed">
                {t('puraDetails.individualDesc2')}
              </p>
              <p className="text-lg text-gray-600 leading-relaxed">
                {t('puraDetails.individualDesc3')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: 'rgb(1, 157, 180)' }}>
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            {t('cta.title')}
          </h2>
          <p className="text-lg text-white/90 mb-8">
            {t('cta.subtitle')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild size="lg" variant="secondary" className="bg-white text-gray-900 hover:bg-gray-100">
              <Link to="/contacts">{t('cta.contact')}</Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="border-white text-white hover:bg-white/10">
              <Link to="/partners">{t('cta.becomePartner')}</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};